/**
 * studyTimeService.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Source of truth: Supabase `study_time_sessions` table.
 *
 * Fix da causa raiz do bug cross-device:
 * - `startStudySession` agora aguarda o userId do Supabase auth.
 * - Se o user ainda não estiver autenticado (ou Supabase offline),
 *   a sessão é salva em `tef_pending_study_sessions` para ser enviada
 *   ao Supabase quando o syncService.runPostLoginSync() for chamado.
 * - `getTodayStudyTime` e `getWeeklyStudyTime` consultam SEMPRE o Supabase
 *   quando o userId é real. Só fazem fallback local se Supabase falhar.
 */
import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";

export interface StudyTimeSession {
  id: string;
  userId: string;
  activityType: string;
  competency: string;
  section?: string | null;
  startedAt: string;
  endedAt?: string | null;
  durationSeconds: number;
  metadata: any;
  createdAt?: string;
}

// Map database column names to JS object keys
const mapRow = (row: any): StudyTimeSession => ({
  id: row.id,
  userId: row.user_id,
  activityType: row.activity_type,
  competency: row.competency,
  section: row.section,
  startedAt: row.started_at,
  endedAt: row.ended_at,
  durationSeconds: row.duration_seconds,
  metadata: row.metadata || {},
  createdAt: row.created_at,
});

// ─── LocalStorage Keys ────────────────────────────────────────────────────────
const LOCAL_STORAGE_KEY = "tef_study_time_sessions";
const PENDING_SESSIONS_KEY = "tef_pending_study_sessions";

class StudyTimeService {
  // ─── Local storage helpers ──────────────────────────────────────────────────

  private getLocalSessions(): StudyTimeSession[] {
    try {
      const data = localStorage.getItem(LOCAL_STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  private saveLocalSessions(sessions: StudyTimeSession[]) {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(sessions));
  }

  private getPendingSessions(): StudyTimeSession[] {
    try {
      const data = localStorage.getItem(PENDING_SESSIONS_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  private savePendingSessions(sessions: StudyTimeSession[]) {
    if (sessions.length === 0) {
      localStorage.removeItem(PENDING_SESSIONS_KEY);
    } else {
      localStorage.setItem(PENDING_SESSIONS_KEY, JSON.stringify(sessions));
    }
  }

  private generateId(): string {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  private getStartOfDay(date: Date = new Date()): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private getStartOfWeek(date: Date = new Date()): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d;
  }

  // ─── Resolve userId from Supabase auth ──────────────────────────────────────

  private async resolveUserId(): Promise<string | null> {
    if (!isSupabaseConfigured || !supabase) return null;
    try {
      const { data } = await supabase.auth.getUser();
      return data?.user?.id ?? null;
    } catch {
      return null;
    }
  }

  // ─── Session lifecycle ───────────────────────────────────────────────────────

  async startStudySession({
    activityType,
    competency,
    section = null,
    metadata = {},
  }: {
    activityType: string;
    competency: string;
    section?: string | null;
    metadata?: any;
  }): Promise<string> {
    const sessionId = this.generateId();
    const now = new Date().toISOString();

    // Resolve real userId (wait for auth)
    const userId = await this.resolveUserId();

    const session: StudyTimeSession = {
      id: sessionId,
      userId: userId ?? "local_user",
      activityType,
      competency,
      section,
      startedAt: now,
      durationSeconds: 0,
      metadata,
      createdAt: now,
    };

    const savedToSupabase = { value: false };

    if (userId && isSupabaseConfigured && supabase) {
      try {
        const { error } = await supabase.from("study_time_sessions").insert({
          id: sessionId,
          user_id: userId,
          activity_type: activityType,
          competency,
          section: section ?? null,
          started_at: now,
          ended_at: null,
          duration_seconds: 0,
          metadata,
        });

        if (error) {
          console.error("[StudyTimeService] Error inserting session to Supabase:", error);
        } else {
          savedToSupabase.value = true;
        }
      } catch (err) {
        console.error("[StudyTimeService] Exception inserting session to Supabase:", err);
      }
    }

    if (!savedToSupabase.value) {
      // Queue for later sync (offline or no auth)
      const pending = this.getPendingSessions();
      pending.push(session);
      this.savePendingSessions(pending);
    }

    console.log("[SYNC_WRITE_STUDY_SESSION]", {
      userId: userId ?? "local_user",
      activityType,
      durationSeconds: 0,
      savedToSupabase: savedToSupabase.value,
      queuedLocal: !savedToSupabase.value,
    });

    return sessionId;
  }

  async updateStudySessionDuration(sessionId: string, durationSeconds: number): Promise<void> {
    // Update in pending queue (if session is still there)
    const pending = this.getPendingSessions();
    const pidx = pending.findIndex((s) => s.id === sessionId);
    if (pidx !== -1) {
      pending[pidx].durationSeconds = durationSeconds;
      this.savePendingSessions(pending);
    }

    // Update in Supabase
    if (isSupabaseConfigured && supabase) {
      try {
        const { error } = await supabase
          .from("study_time_sessions")
          .update({ duration_seconds: durationSeconds })
          .eq("id", sessionId);

        if (error) {
          console.error("[StudyTimeService] Error updating session duration in Supabase:", error);
        }
      } catch (err) {
        console.error("[StudyTimeService] Exception updating session duration in Supabase:", err);
      }
    }
  }

  async endStudySession(sessionId: string): Promise<void> {
    const now = new Date().toISOString();

    // End in pending queue (if session is still there)
    const pending = this.getPendingSessions();
    const pidx = pending.findIndex((s) => s.id === sessionId);
    if (pidx !== -1) {
      pending[pidx].endedAt = now;
      this.savePendingSessions(pending);
    }

    // End in Supabase
    if (isSupabaseConfigured && supabase) {
      try {
        const { error } = await supabase
          .from("study_time_sessions")
          .update({ ended_at: now })
          .eq("id", sessionId);

        if (error) {
          console.error("[StudyTimeService] Error ending session in Supabase:", error);
        }
      } catch (err) {
        console.error("[StudyTimeService] Exception ending session in Supabase:", err);
      }
    }
  }

  // ─── Read: Dashboard queries ─────────────────────────────────────────────────

  async getTodayStudyTime(userId: string): Promise<number> {
    const today = this.getStartOfDay();

    if (isSupabaseConfigured && supabase && userId && userId !== "local_user") {
      try {
        const { data, error } = await supabase
          .from("study_time_sessions")
          .select("duration_seconds")
          .eq("user_id", userId)
          .gte("started_at", today.toISOString());

        if (error) throw error;

        const total = (data || []).reduce((acc, curr) => acc + (curr.duration_seconds || 0), 0);

        console.log("[SYNC_SOURCE_STUDY_TIME]", {
          source: "supabase",
          period: "today",
          userId,
          todaySeconds: total,
        });

        return total;
      } catch (err) {
        console.warn("[StudyTimeService] Supabase offline or error, falling back to local storage:", err);
      }
    }

    // Local fallback (offline or unauthenticated)
    const allLocal = [...this.getLocalSessions(), ...this.getPendingSessions()];
    const total = allLocal
      .filter((s) => (s.userId === userId || s.userId === "local_user") && new Date(s.startedAt) >= today)
      .reduce((acc, curr) => acc + curr.durationSeconds, 0);

    console.log("[SYNC_SOURCE_STUDY_TIME]", {
      source: "localStorage_fallback",
      period: "today",
      userId,
      todaySeconds: total,
    });

    return total;
  }

  async getWeeklyStudyTime(userId: string): Promise<number> {
    const startOfWeek = this.getStartOfWeek();

    if (isSupabaseConfigured && supabase && userId && userId !== "local_user") {
      try {
        const { data, error } = await supabase
          .from("study_time_sessions")
          .select("duration_seconds")
          .eq("user_id", userId)
          .gte("started_at", startOfWeek.toISOString());

        if (error) throw error;

        const total = (data || []).reduce((acc, curr) => acc + (curr.duration_seconds || 0), 0);

        console.log("[SYNC_SOURCE_STUDY_TIME]", {
          source: "supabase",
          period: "week",
          userId,
          weekSeconds: total,
        });

        return total;
      } catch (err) {
        console.warn("[StudyTimeService] Supabase offline or error, falling back to local storage:", err);
      }
    }

    const allLocal = [...this.getLocalSessions(), ...this.getPendingSessions()];
    const total = allLocal
      .filter((s) => (s.userId === userId || s.userId === "local_user") && new Date(s.startedAt) >= startOfWeek)
      .reduce((acc, curr) => acc + curr.durationSeconds, 0);

    console.log("[SYNC_SOURCE_STUDY_TIME]", {
      source: "localStorage_fallback",
      period: "week",
      userId,
      weekSeconds: total,
    });

    return total;
  }

  async getStudyTimeByCompetency(userId: string): Promise<{
    reading: number;
    listening: number;
    writing: number;
    flashcards: number;
    errorBank: number;
    general: number;
  }> {
    const startOfWeek = this.getStartOfWeek();
    let weeklySessions: StudyTimeSession[] = [];

    if (isSupabaseConfigured && supabase && userId && userId !== "local_user") {
      try {
        const { data, error } = await supabase
          .from("study_time_sessions")
          .select("*")
          .eq("user_id", userId)
          .gte("started_at", startOfWeek.toISOString());

        if (error) throw error;
        weeklySessions = (data || []).map(mapRow);
      } catch (err) {
        console.warn("[StudyTimeService] Supabase offline or error, falling back to local storage:", err);
        const allLocal = [...this.getLocalSessions(), ...this.getPendingSessions()];
        weeklySessions = allLocal.filter(
          (s) => (s.userId === userId || s.userId === "local_user") && new Date(s.startedAt) >= startOfWeek
        );
      }
    } else {
      const allLocal = [...this.getLocalSessions(), ...this.getPendingSessions()];
      weeklySessions = allLocal.filter(
        (s) => (s.userId === userId || s.userId === "local_user") && new Date(s.startedAt) >= startOfWeek
      );
    }

    const counts = {
      reading: 0,
      listening: 0,
      writing: 0,
      flashcards: 0,
      errorBank: 0,
      general: 0,
    };

    weeklySessions.forEach((s) => {
      if (s.competency === "reading") counts.reading += s.durationSeconds;
      else if (s.competency === "listening") counts.listening += s.durationSeconds;
      else if (s.competency === "writing") counts.writing += s.durationSeconds;

      if (s.activityType === "flashcards") counts.flashcards += s.durationSeconds;
      else if (s.activityType === "error_bank") counts.errorBank += s.durationSeconds;
      else if (s.competency === "general") counts.general += s.durationSeconds;
    });

    return counts;
  }

  async getStudyTimeHistory(userId: string, days = 30): Promise<{ date: string; durationSeconds: number }[]> {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    cutoffDate.setHours(0, 0, 0, 0);

    let historySessions: StudyTimeSession[] = [];

    if (isSupabaseConfigured && supabase && userId && userId !== "local_user") {
      try {
        const { data, error } = await supabase
          .from("study_time_sessions")
          .select("*")
          .eq("user_id", userId)
          .gte("started_at", cutoffDate.toISOString());

        if (error) throw error;
        historySessions = (data || []).map(mapRow);
      } catch (err) {
        console.warn("[StudyTimeService] Supabase offline or error, falling back to local storage:", err);
        const allLocal = [...this.getLocalSessions(), ...this.getPendingSessions()];
        historySessions = allLocal.filter(
          (s) => (s.userId === userId || s.userId === "local_user") && new Date(s.startedAt) >= cutoffDate
        );
      }
    } else {
      const allLocal = [...this.getLocalSessions(), ...this.getPendingSessions()];
      historySessions = allLocal.filter(
        (s) => (s.userId === userId || s.userId === "local_user") && new Date(s.startedAt) >= cutoffDate
      );
    }

    // Aggregate by date (YYYY-MM-DD local time)
    const map = new Map<string, number>();

    // Pre-fill last N days
    for (let i = 0; i < days; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split("T")[0];
      map.set(dateStr, 0);
    }

    historySessions.forEach((s) => {
      const dateStr = new Date(s.startedAt).toISOString().split("T")[0];
      if (map.has(dateStr)) {
        map.set(dateStr, (map.get(dateStr) || 0) + s.durationSeconds);
      }
    });

    return Array.from(map.entries())
      .map(([date, durationSeconds]) => ({ date, durationSeconds }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  async getAdminStudyStats(): Promise<{
    totalStudyTimeThisWeek: number;
    averageStudyTimePerUser: number;
    mostStudiedActivity: string;
    activeLearners: {
      userId: string;
      userName: string;
      userEmail: string;
      totalStudyTime: number;
    }[];
  }> {
    const startOfWeek = this.getStartOfWeek();
    let sessions: StudyTimeSession[] = [];
    const userProfilesMap = new Map<string, { name: string; email: string }>();

    if (isSupabaseConfigured && supabase) {
      try {
        const { data: sessionsData, error: sessionsErr } = await supabase
          .from("study_time_sessions")
          .select("*")
          .gte("started_at", startOfWeek.toISOString());

        if (sessionsErr) throw sessionsErr;
        sessions = (sessionsData || []).map(mapRow);

        const { data: profilesData } = await supabase
          .from("user_profiles")
          .select("id, name, email");

        if (profilesData) {
          profilesData.forEach((p) => {
            userProfilesMap.set(p.id, {
              name: p.name || p.email.split("@")[0],
              email: p.email,
            });
          });
        }
      } catch (err) {
        console.error("[StudyTimeService] Error fetching admin stats from Supabase:", err);
        const allLocal = [...this.getLocalSessions(), ...this.getPendingSessions()];
        sessions = allLocal.filter((s) => new Date(s.startedAt) >= startOfWeek);
      }
    } else {
      const allLocal = [...this.getLocalSessions(), ...this.getPendingSessions()];
      sessions = allLocal.filter((s) => new Date(s.startedAt) >= startOfWeek);
    }

    const totalStudyTimeThisWeek = sessions.reduce((acc, s) => acc + s.durationSeconds, 0);

    const userDurations = new Map<string, number>();
    sessions.forEach((s) => {
      userDurations.set(s.userId, (userDurations.get(s.userId) || 0) + s.durationSeconds);
    });

    const uniqueUsersCount = userDurations.size;
    const averageStudyTimePerUser =
      uniqueUsersCount > 0 ? Math.round(totalStudyTimeThisWeek / uniqueUsersCount) : 0;

    const activityDurations = new Map<string, number>();
    sessions.forEach((s) => {
      activityDurations.set(s.activityType, (activityDurations.get(s.activityType) || 0) + s.durationSeconds);
    });

    let mostStudiedActivity = "N/A";
    let maxActivityDuration = -1;
    activityDurations.forEach((duration, act) => {
      if (duration > maxActivityDuration) {
        maxActivityDuration = duration;
        mostStudiedActivity = act;
      }
    });

    const activityTranslations: Record<string, string> = {
      reading_test: "Compreensão Escrita (CE)",
      listening_test: "Compreensão Oral (CO)",
      writing_practice: "Expressão Escrita (EE)",
      flashcards: "Flashcards",
      error_bank: "Banco de Erros",
      history_review: "Revisão de Histórico",
      study_plan: "Plano de Estudo",
      writing_review: "Revisão de Redações",
    };
    mostStudiedActivity = activityTranslations[mostStudiedActivity] || mostStudiedActivity;

    const activeLearners: {
      userId: string;
      userName: string;
      userEmail: string;
      totalStudyTime: number;
    }[] = [];

    userDurations.forEach((duration, uid) => {
      const prof = userProfilesMap.get(uid) || { name: "Usuário Local", email: "local@teftrainer.ca" };
      activeLearners.push({
        userId: uid,
        userName: prof.name,
        userEmail: prof.email,
        totalStudyTime: duration,
      });
    });

    activeLearners.sort((a, b) => b.totalStudyTime - a.totalStudyTime);

    return {
      totalStudyTimeThisWeek,
      averageStudyTimePerUser,
      mostStudiedActivity,
      activeLearners,
    };
  }
}

export const studyTimeService = new StudyTimeService();
